import { isAxiosError } from 'axios';

import { useQuery } from '@tanstack/react-query';

import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import http from 'services/http';

export type NewHospitalStatus = 'ready' | 'pick_client' | 'which_hospital' | 'created' | 'declined';
export type NewHospitalTab = 'review' | 'created' | 'declined';
export type DeclineReason = 'existing_group' | 'not_hospital' | 'dont_create';

export type NewHospitalAddress = {
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};

export type FilingOption =
  | {
      key: string;
      kind: 'existing_client';
      clientId: string;
      clientName: string;
      hospitalCount: number;
      states: string[];
      existingLocationId: string | null;
      clientUsersCount?: number;
    }
  | { key: string; kind: 'new_client'; proposedName: string; existingLocationId?: null; clientUsersCount?: number };

export type Filing = { kind: 'existing_client'; clientId: string } | { kind: 'new_client'; name: string };

export type FilingPick = { filingKey: string; confidence: number; filing?: Filing | null };

export type NewHospitalReceived = {
  importConfigurationId: string;
  clientId: string;
  clientName: string;
  key: string;
  sourceSystem: string | null;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
};

export type NewHospitalAlternative = {
  ccn: string;
  name: string;
  address: NewHospitalAddress | null;
  distanceMiles: number | null;
};

export type NewHospital = {
  ccn: string;
  name: string;
  address: NewHospitalAddress | null;
  providerType: string | null;
  parentCcn: string | null;
  healthSystem: { id: string; name: string } | null;
  status: NewHospitalStatus;
  draftedAt: string | null;
  version: string;
  received: NewHospitalReceived[];
  filing: Filing | null;
  filingOptions: FilingOption[];
  rules: FilingPick | null;
  jev: FilingPick | null;
  identityConfidence: number | null;
  alternatives: NewHospitalAlternative[];
  closestOlioHospital: { groupId: string; name: string; distanceMiles: number | null } | null;
  declinedReason: DeclineReason | null;
  decidedBy: { id: string; name: string } | null;
  decidedAt: string | null;
  groupId: string | null;
  createdCcn?: string | null;
  createdHospital?: { ccn: string; name: string; address: NewHospitalAddress | null } | null;
};

export type ApproveNewHospital = { version: string; ccn: string; filing: Filing };
export type DeclineNewHospital = { version: string; reason: DeclineReason; groupId?: string };

export const adminNewHospitalsQueryKeys = {
  all: ['adminNewHospitals'] as const,
  index: (status: NewHospitalTab, ccn?: string) => ['adminNewHospitals', 'index', status, ccn ?? null] as const,
  count: ['adminNewHospitals', 'count'] as const,
};

const URL = 'admin/new_hospitals';

export async function adminIndexNewHospitals(params: { status?: NewHospitalTab; ccn?: string }) {
  return http
    .get<NewHospital[] | { data: NewHospital[] }>(URL, { params })
    .then((res) => (Array.isArray(res.data) ? res.data : (res.data.data ?? [])));
}

export async function adminNewHospitalsCount() {
  return http.get<{ count: number }>(`${URL}/count`).then((res) => res.data.count);
}

export async function adminApproveNewHospital(ccn: string, body: ApproveNewHospital) {
  return http.post(`${URL}/${encodeURIComponent(ccn)}/approve`, body).then((res) => res.data);
}

export async function adminDeclineNewHospital(ccn: string, body: DeclineNewHospital) {
  return http.post(`${URL}/${encodeURIComponent(ccn)}/decline`, body).then((res) => res.data);
}

export const NEW_HOSPITALS_PATH = '/admin/new-hospitals';
export const NEW_HOSPITALS_ACCESSORS = [PERMISSIONS.adminClientCreate, PERMISSIONS.adminGroupCreate];

export function useCanReviewNewHospitals() {
  const { profile } = useProfile();
  return profile.isAdmin && profile.hasAll(NEW_HOSPITALS_ACCESSORS);
}

export function useNewHospitalsCount(enabled = true) {
  const allowed = useCanReviewNewHospitals();
  return useQuery({
    queryKey: adminNewHospitalsQueryKeys.count,
    queryFn: adminNewHospitalsCount,
    enabled: allowed && enabled,
    retry: false,
  }).data;
}

export type NewHospitalWriteError = { code: string | null; message: string | null; groupId: string | null };

export function newHospitalWriteError(error: unknown): NewHospitalWriteError {
  const data = isAxiosError(error)
    ? (error.response?.data as { error?: unknown; message?: unknown; groupId?: unknown } | undefined)
    : undefined;
  const text = (v: unknown) => (typeof v === 'string' && v ? v : null);
  return { code: text(data?.error), message: text(data?.message), groupId: text(data?.groupId) };
}
